import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, CircleDashed, Loader2, PlugZap, RefreshCw, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIntegrations, useIntegrationTest, type CacheStatus, type IntegrationStatus, type IntegrationTest } from '@/api/integrations';
import { fmtAge } from '@/lib/rmm-helpers';
import { cn } from '@/lib/utils';

/**
 * Admin → Integrations: every external system Cosmos talks to. Configuration (which env vars are
 * set; secret values never leave the server), a live connection test per vendor (one cheap call,
 * run when the page opens), background caches with age / load time / last error, and worker and
 * debug switches. Fix configuration in App Service → Application Settings.
 */

type Health = 'ok' | 'degraded' | 'failing' | 'testing' | 'configured' | 'off';

const HEALTH: Record<Health, { label: string; cls: string; Icon: typeof CheckCircle2 }> = {
  ok:         { label: 'Connected',      cls: 'bg-success-soft text-success',              Icon: CheckCircle2 },
  degraded:   { label: 'Degraded',       cls: 'bg-warning-soft text-warning',              Icon: AlertTriangle },
  failing:    { label: 'Failing',        cls: 'bg-destructive/10 text-destructive',        Icon: XCircle },
  testing:    { label: 'Testing…',       cls: 'bg-muted text-muted-foreground',            Icon: Loader2 },
  configured: { label: 'Configured',     cls: 'bg-muted text-foreground',                  Icon: CheckCircle2 },
  off:        { label: 'Not configured', cls: 'bg-muted text-muted-foreground',            Icon: CircleDashed },
};

function healthOf(i: IntegrationStatus, test: IntegrationTest | undefined, testing: boolean): Health {
  if (!i.configured) return 'off';
  if (i.testable && testing && !test) return 'testing';
  if (test && !test.ok) return 'failing';
  if (i.caches.some((c) => c.lastError)) return 'degraded';
  return i.testable && test ? 'ok' : 'configured';
}

export default function AdminIntegrationsPage() {
  const { data, isLoading, error } = useIntegrations();
  const qc = useQueryClient();
  const retestAll = () => qc.invalidateQueries({ queryKey: ['admin', 'integrations'] });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold">Integrations</h1>
          <p className="text-xs text-muted-foreground">Every external system Cosmos connects to. Settings live in App Service → Application Settings (or <code>.env</code> in dev).</p>
        </div>
        <Button variant="ghost" size="sm" onClick={retestAll}>
          <RefreshCw className="mr-1.5 h-3.5 w-3.5" />Re-test all
        </Button>
      </div>

      {isLoading && <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Failed to load integrations'}
        </div>
      )}
      {data && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {data.integrations.map((i) => <IntegrationCard key={i.id} integration={i} />)}
        </div>
      )}
    </div>
  );
}

function IntegrationCard({ integration: i }: { integration: IntegrationStatus }) {
  const test = useIntegrationTest(i.id, i.configured && i.testable);
  const health = healthOf(i, test.data, test.isFetching);
  const h = HEALTH[health];
  const missing = i.env.filter((e) => e.required && !e.set);

  return (
    <section className="rounded-lg border border-border bg-card shadow-sm">
      <div className="flex items-start justify-between gap-3 border-b border-divider px-4 py-3">
        <div className="min-w-0">
          <h2 className="text-sm font-semibold text-foreground">{i.label}</h2>
          <p className="text-[11px] text-muted-foreground">{i.vendor} · used by {i.modules.join(', ')}</p>
        </div>
        <span className={cn('inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold', h.cls)}>
          <h.Icon className={cn('h-3 w-3', health === 'testing' && 'animate-spin')} />{h.label}
        </span>
      </div>

      <div className="space-y-3 px-4 py-3 text-xs">
        {/* Live test */}
        {i.testable && i.configured && (
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              {test.data ? (
                test.data.ok
                  ? <p className="text-foreground">{test.data.detail} <span className="text-muted-foreground">· {fmtMs(test.data.ms)}</span></p>
                  : <p className="break-words text-destructive">{test.data.error} <span className="text-muted-foreground">· {fmtMs(test.data.ms)}</span></p>
              ) : test.error ? (
                <p className="text-destructive">Test request failed: {test.error instanceof Error ? test.error.message : 'unknown error'}</p>
              ) : (
                <p className="text-muted-foreground">Testing the connection…</p>
              )}
              {test.data && <p className="text-[11px] text-muted-foreground">Tested {fmtAge(test.data.at)} ago</p>}
            </div>
            <Button variant="outline" size="sm" className="h-7 shrink-0" onClick={() => test.refetch()} disabled={test.isFetching}>
              {test.isFetching ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <PlugZap className="mr-1 h-3.5 w-3.5" />}Test
            </Button>
          </div>
        )}
        {!i.configured && (
          <p className="text-muted-foreground">
            {missing.length ? <>Missing: {missing.map((e) => <code key={e.name} className="mr-1">{e.name}</code>)}</> : 'Not configured.'}
            {' '}Its pages show as unavailable until it's set up.
          </p>
        )}

        {/* Settings */}
        <div>
          <Label>Settings</Label>
          <div className="flex flex-wrap gap-1.5">
            {i.env.map((e) => (
              <span key={e.name} title={e.set ? (e.value ?? 'Set (value hidden)') : e.required ? 'Required, not set' : 'Optional, not set'}
                className={cn('inline-flex max-w-full items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10.5px]',
                  e.set ? 'border-border text-foreground' : e.required ? 'border-destructive/40 text-destructive' : 'border-dashed border-border text-muted-foreground')}>
                {e.set ? <CheckCircle2 className="h-3 w-3 shrink-0 text-success" /> : <XCircle className="h-3 w-3 shrink-0" />}
                <span className="truncate">{e.name}{e.value && <span className="text-muted-foreground"> = {e.value}</span>}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Background work */}
        {(i.workers.length > 0 || i.debug) && (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {i.workers.map((w) => (
              <span key={w.env} title={`${w.env}=${w.on ? 'on (default)' : 'false'}`} className={w.on ? 'text-foreground' : 'text-warning'}>
                {w.label}: <span className="font-medium">{w.on ? 'on' : 'off'}</span>
              </span>
            ))}
            {i.debug && (
              <span title={i.debug.env} className={i.debug.on ? 'text-warning' : 'text-muted-foreground'}>
                Debug logging: <span className="font-medium">{i.debug.on ? 'on' : 'off'}</span>
              </span>
            )}
          </div>
        )}

        {i.caches.length > 0 && (
          <div>
            <Label>Caches</Label>
            <table className="w-full">
              <tbody className="divide-y divide-divider">
                {i.caches.map((c) => <CacheRow key={c.name} cache={c} />)}
              </tbody>
            </table>
          </div>
        )}

        {i.notes.map((n) => <p key={n} className="text-[11px] text-muted-foreground">{n}</p>)}
      </div>
    </section>
  );
}

function CacheRow({ cache: c }: { cache: CacheStatus }) {
  const age = c.loadedAt ? Date.now() - new Date(c.loadedAt).getTime() : null;
  return (
    <tr className="align-top">
      <td className="py-1 pr-3 capitalize text-foreground">{c.name}</td>
      <td className="py-1 pr-3 text-muted-foreground">
        {c.loading ? <span className="inline-flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" />loading</span>
          : c.loadedAt ? <span title={`Refreshes every ${fmtMs(c.ttlMs)} when used`}>{fmtAge(c.loadedAt)} old{age != null && age > c.ttlMs && ' · refreshes on next use'}</span>
          : 'not loaded yet'}
      </td>
      <td className="whitespace-nowrap py-1 pr-3 text-right tabular-nums text-muted-foreground">{c.loadMs != null ? fmtMs(c.loadMs) : ''}</td>
      <td className="py-1 text-right">
        {c.lastError && <span className="whitespace-nowrap text-destructive" title={`${c.lastError} (${c.lastErrorAt ? new Date(c.lastErrorAt).toLocaleString() : ''})`}>last refresh failed</span>}
      </td>
    </tr>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return <div className="mb-1 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{children}</div>;
}

function fmtMs(ms: number) {
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return `${Math.round(ms / 60_000)} min`;
}
